import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Image, useScrollPosition } from '@as/cotopaxi';
import { useAemConfig } from '@as-react/common-components';
import { ProductImageSizesPortraitEnum } from '@as-react/common-types';
import { ArrowRightIcon } from '@yonderland/andes-icons';
import { Icon, Link } from '@yonderland/andes-react';
import { Button, Box, Popover, Scrollable, Stack, useMediaQueryMatchesOnDesktop, TextAlpha, } from '@yonderland/andes-react';
import { useEffect, useLayoutEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NAVIGATION_HORIZONTAL, NAVIGATION_HORIZONTAL_COLLAPSE } from '../../../constants/DOMElementsConstants';
import { DELAY } from '../../../constants/GeneralConstants';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { ProductImageHeight, ProductImageWidth } from '../../../constants/ProductConstants';
import { MIN_SCREEN_WIDTH_DESKTOP } from '../../../constants/ScreenWidthConstants';
import { EventActions } from '../../../enums/AnalyticsEnums';
import { useOAuth } from '../../../hooks/useOAuth';
import { analyticsActions } from '../../../redux/analytics/actions/AnalyticsActions.creators';
import { makeGetBasketMappedProductsState, makeGetBasketTotalQuantityState, } from '../../../redux/basket/selectors/BasketSelector';
import { makeGetFirstName } from '../../../redux/session/selectors/SessionSelector';
import BrowserUtil from '../../../util/BrowserUtil';
import { getImageSourceSetByProductCode } from '../../../util/ImageUtil';
import { generateTestId } from '../../../util/TestUtil';
const TEST_ID = 'basket-reminder-notification';
const MAX_PRODUCTS = 5;
const POPOVER_OFFSET_DEFAULT = 24;
const POPOVER_OFFSET_COLLAPSED_NAV = 64;
const DESKTOP_IMAGE_WIDTH = 90;
const DESKTOP_IMAGE_HEIGHT = 136;
const USER_AGENT_FIREFOX = 'Firefox';
const overflowOptions = (_floatingElement, overflowValues, translate) => {
    if (!overflowValues || !translate)
        return;
    const isFixedPosition = overflowValues.top >= -POPOVER_OFFSET_DEFAULT;
    return {
        position: 'fixed',
        transform: `translate(${translate.x}px, ${isFixedPosition ? POPOVER_OFFSET_DEFAULT : Math.abs(overflowValues.top)}px)`,
    };
};
export const BasketReminderNotification = ({ hrefViewCart, children }) => {
    const { productImageConfig } = useAemConfig();
    const { t } = useTranslation(I18N_NAMESPACES.checkout);
    const products = useSelector(makeGetBasketMappedProductsState());
    const { isLoggedIn, userInfo } = useOAuth();
    const basketAmount = useSelector(makeGetBasketTotalQuantityState());
    const firstName = useSelector(makeGetFirstName());
    const [open, setOpen] = useState(false);
    const [popoverOffset, setPopoverOffset] = useState(POPOVER_OFFSET_DEFAULT);
    const [navigationEl, setNavigationEl] = useState(null);
    const dispatch = useDispatch();
    const isDesktop = useMediaQueryMatchesOnDesktop();
    const isFirefox = BrowserUtil.detectBrowser(USER_AGENT_FIREFOX);
    const onViewBasketClick = (event) => {
        event === null || event === void 0 ? void 0 : event.preventDefault();
        dispatch(analyticsActions.basketReminderNotificationEvent(EventActions.BASKET_REMINDER_NOTIFICATION_CLICK));
        window.location.href = hrefViewCart;
    };
    useLayoutEffect(() => {
        setTimeout(() => {
            setOpen(true);
            dispatch(analyticsActions.basketReminderNotificationViewed());
        }, DELAY.DELAY_300);
    }, []);
    const popoverOffsetHandler = (navigation) => {
        if (!navigation)
            return;
        const isNavCollapsed = navigation.classList.contains(NAVIGATION_HORIZONTAL_COLLAPSE);
        setPopoverOffset(isNavCollapsed ? POPOVER_OFFSET_COLLAPSED_NAV : POPOVER_OFFSET_DEFAULT);
    };
    useEffect(() => {
        const navigation = document.querySelector(NAVIGATION_HORIZONTAL);
        setNavigationEl(navigation);
        popoverOffsetHandler(navigation);
    }, []);
    useScrollPosition(() => {
        popoverOffsetHandler(navigationEl);
    }, [navigationEl]);
    const greetingText = () => {
        var _a;
        return `${isLoggedIn
            ? t(`${I18N_NAMESPACES.checkout}:basket.reminder.greeting.loggedIn`, {
                firstName: (_a = userInfo === null || userInfo === void 0 ? void 0 : userInfo.given_name) !== null && _a !== void 0 ? _a : firstName,
            })
            : t(`${I18N_NAMESPACES.checkout}:basket.reminder.greeting.notLoggedIn`)} 👋`;
    };
    return (_jsxs(Popover, { isOpen: open, hasCloseBtn: true, animated: true, placement: "bottom-end", overflowOptions: isDesktop || isFirefox ? overflowOptions : undefined, offsetOptions: { mainAxis: popoverOffset }, disableTrigger: true, testId: TEST_ID, children: [_jsx(Popover.Trigger, { children: children }), _jsx(Popover.Header, { children: _jsx(Box, { paddingBlockStart: "1", paddingBlockEnd: { base: '1', lg: '3' }, children: _jsx(TextAlpha, { as: "h2", variant: "subheadingM", children: greetingText() }) }) }), _jsxs(Popover.Body, { children: [_jsx(TextAlpha, { color: "subtle", children: t(`${I18N_NAMESPACES.checkout}:basket.reminder.message`, { count: basketAmount }) }), _jsx(Box, { paddingBlockStart: { base: '2', lg: '4' }, paddingBlockEnd: { base: '0', lg: '2' }, children: _jsx(Link, { href: hrefViewCart, onClick: onViewBasketClick, testId: generateTestId({ testId: TEST_ID, suffix: 'view-basket-btn' }), children: _jsx(Scrollable, { horizontal: true, clipped: true, children: _jsx("div", { style: { height: isDesktop ? DESKTOP_IMAGE_HEIGHT : ProductImageHeight.THUMB }, children: _jsx(Stack, { gap: "2", wrap: "wrap", children: products.slice(0, MAX_PRODUCTS).map(product => {
                                            const imageUrl = getImageSourceSetByProductCode(product.productCode, product.image, productImageConfig);
                                            return (_jsx(Image, { src: imageUrl[ProductImageSizesPortraitEnum.thumb], srcset: {
                                                    [`${MIN_SCREEN_WIDTH_DESKTOP}w`]: imageUrl[ProductImageSizesPortraitEnum.bundles],
                                                }, width: isDesktop ? DESKTOP_IMAGE_WIDTH : ProductImageWidth.THUMB, height: isDesktop ? DESKTOP_IMAGE_HEIGHT : ProductImageHeight.THUMB, alt: product.productCode }, product.sku));
                                        }) }) }) }) }) })] }), _jsx(Popover.Footer, { children: _jsxs(Button, { theme: "branded", fullWidth: true, onClick: onViewBasketClick, testId: generateTestId({ testId: TEST_ID, suffix: 'cta-btn' }), children: [t(`${I18N_NAMESPACES.checkout}:basket.reminder.ctaLabel`), _jsx(Icon, { source: ArrowRightIcon })] }) })] }));
};
